import { useForm } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, MapPinIcon, SelectOption, SearchIcon, Input } from "@mwi/ui";

import { RootState } from "store";
import { useGetSitesQuery } from "api";
import BreedsVocabulary from "./breeds";
import SpeciesVocabulary from "./species";
import ReasonsVocabulary from "./reasons";
import useSearchQuery from "hooks/useSearchQuery";
import { setFilters, setPagination } from "slices/vocabularyFilters";
import { VocabularyCategories } from "types/vocabulary/categories";
import useTitle from "hooks/useTitle";

type Inputs = {
  category: VocabularyCategories;
  site: string;
  search: string;
};

const VocabularyPage = (): JSX.Element => {
  useTitle("Vocabulary");

  const dispatch = useDispatch();

  const { filtering, pagination } = useSelector((state: RootState) => ({
    filtering: state.vocabularyFilters.filtering,
    pagination: state.vocabularyFilters.pagination,
  }));

  const { data: sites } = useGetSitesQuery();

  const { control, register, watch, setValue, getValues } = useForm<Inputs>({
    defaultValues: {
      category: "reasons",
      site: filtering.location_uuid,
      search: filtering.search,
    },
  });

  const [debouncedSearch] = useDebounce(watch("search"), 400);

  const searchParams = useMemo(
    () => ({
      category: filtering.category,
      page: pagination.page,
      search: filtering.search,
      site:
        filtering.category === "reasons" && sites?.length
          ? sites[0].id
          : undefined,
    }),
    [
      filtering.category,
      filtering.search,
      filtering.location_uuid,
      pagination.page,
    ],
  );

  useSearchQuery(searchParams);

  useEffect(() => {
    // reset pagination to first page when changing vocabulary type
    if (filtering.category !== getValues("category")) {
      const defaultSite = sites?.length ? sites[0]?.id : "";

      dispatch(setPagination({ page: 1 }));
      dispatch(
        setFilters({
          category: getValues("category"),
          search: getValues("search"),
          location_uuid: defaultSite,
        }),
      );
      setValue("site", defaultSite);
    } else {
      dispatch(
        setFilters({
          category: getValues("category"),
          search: getValues("search"),
          location_uuid: getValues("site"),
        }),
      );
    }
  }, [watch("category"), watch("site")]);

  useEffect(() => {
    // Reset pagination back to first page on each search
    if (debouncedSearch !== filtering.search) {
      dispatch(setPagination({ page: 1 }));
      dispatch(
        setFilters({
          category: getValues("category"),
          search: getValues("search"),
          location_uuid: getValues("site"),
        }),
      );
    }
  }, [debouncedSearch]);

  const vocabularyTypeOptions = useMemo(
    () => [
      { id: "reasons", display: "Appointments" },
      { id: "species", display: "Species" },
      { id: "breeds", display: "Breeds" },
    ],
    [],
  );

  useEffect(() => {
    if (sites && sites?.length && !getValues("site")) {
      setValue("site", sites[0]?.id);
    }
  }, [sites]);

  return (
    <div className="grid tablet:grid-cols-8 desktop:grid-cols-10 !pb-16 gap-x-6">
      <div className="col-span-full mb-14 gap-y-[2px] flex flex-col text-desktop">
        <p className="font-semibold">
          Manage what your clients see when booking appointments
        </p>
        <p>Change the display name for different appointment reasons</p>
        <p>
          Add an optional message that will appear when pet owners select a
          reason
        </p>
      </div>

      <div className="col-span-full tablet:col-span-2 tablet:col-start-1">
        <Select
          name="category"
          size="small"
          control={control}
          placeholder="Select vocab category"
        >
          {vocabularyTypeOptions?.map((vocabType) => (
            <SelectOption key={vocabType.id} value={vocabType.id}>
              {vocabType.display}
            </SelectOption>
          ))}
        </Select>
      </div>

      {watch("category") === "reasons" && (
        <div className="col-span-full tablet:col-start-5 tablet:col-end-7 desktop:col-start-6 desktop:col-end-8">
          <Select
            name="site"
            size="small"
            control={control}
            prefixIcon={<MapPinIcon />}
            placeholder="Select a vet site"
          >
            {sites?.map((s) => (
              <SelectOption key={s.id} value={s.id}>
                {s.name}
              </SelectOption>
            ))}
          </Select>
        </div>
      )}

      <div className="col-span-full tablet:col-start-7 tablet:col-end-9 desktop:col-start-8 desktop:col-end-11">
        <Input
          size="small"
          placeHolder="Search"
          {...register("search")}
          prefixIcon={<SearchIcon />}
        />
      </div>

      <div className="mt-8 col-span-full">
        {watch("category") === "reasons" && <ReasonsVocabulary />}
        {watch("category") === "species" && <SpeciesVocabulary />}
        {watch("category") === "breeds" && <BreedsVocabulary />}
      </div>
    </div>
  );
};

export default VocabularyPage;
